import React from 'react';
import PropTypes from 'prop-types';
import { DialogContent, DialogTitle, Divider } from '@material-ui/core';
import {
  Button,
  Dialog,
  Flex,
  IconButton,
  Typography,
} from '@knockrentals/knock-shared-web-v2';
import { FileDropzone } from '../FileDropzone';
import ImageCard from './ImageCard';
import ImageDropCard from './ImageDropCard';
import ImageUploadCard from './ImageUploadCard';
import ImageValidationErrorCard from './ImageValidationErrorCard';
import { useImageManager } from './use-image-manager';
import { makeCss } from '../../../../knock-design-system';
import { IMAGE_DIALOG_TYPES } from '../constants';
import {
  ADD_IMAGE_TEXT,
  DIALOG_CARD_LABELS,
  IMAGE_DIALOG_SUBTITLES,
  IMAGE_DIALOG_TITLES,
  PHOTO_UPLOAD_KEYS,
} from './constants';

export const HIDDEN_INPUT_LABEL = 'image upload';

const useCss = makeCss((theme) => ({
  dialogActions: {
    justifyContent: 'space-between',
  },
  dialogContent: {
    height: '411px',
    overflowY: 'auto',
  },
  dropWrapper: {
    height: 'inherit',
    flex: 1,
  },
}));

const ImageManager = ({
  closeDialog,
  dialogType,
  images = [],
  updateImages,
}) => {
  const css = useCss();

  const {
    abortAllUploads,
    acceptedFileFormats,
    clearDropCardErrorMessage,
    clearFileValidationError,
    currentImages,
    deleteCurrentImage,
    deleteImageFile,
    dropCardErrorMessage,
    dropFrameRef,
    fileInputRef,
    fileValidationErrors,
    handleAddPhotosOnClick,
    handleFile,
    handleDropTargetClick,
    imageFiles,
    isSavedButtonDisabled,
    maxDropFileCount,
    onImageUpload,
    resetImageFiles,
    updateCurrentImageDescription,
    uploadRefCallback,
  } = useImageManager({
    images,
    dialogType,
  });

  const handleCancelOnClick = () => {
    abortAllUploads();
    closeDialog();
  };

  const handleSaveOnClick = () => {
    updateImages(currentImages);
    closeDialog();
  };

  const isAddPhotosActive = imageFiles?.length;
  const isDescriptionVisible = dialogType === IMAGE_DIALOG_TYPES.PHOTOS;
  const isDropAreaVisible = maxDropFileCount > 0;
  const isEmpty =
    !imageFiles?.length &&
    !currentImages.length &&
    !fileValidationErrors?.length;

  const isFullWidthDropCard = dialogType === IMAGE_DIALOG_TYPES.LOGO || isEmpty;

  const getHandleDropTargetClick = () => {
    if (!isEmpty) {
      return handleDropTargetClick;
    }
  };

  return (
    <React.Fragment>
      <DialogTitle disableTypography>
        <Flex
          justifyContent="space-between"
          alignItems="center"
          gap={16}
          width="100%"
        >
          <Typography variant="h6">
            {IMAGE_DIALOG_TITLES[dialogType]}
          </Typography>

          <IconButton
            onClick={handleCancelOnClick}
            iconName="close-md"
            iconSize="medium"
            aria-label="close dialog"
          />
        </Flex>
      </DialogTitle>

      <Divider />
      <DialogContent css={css.dialogContent}>
        <Flex flexDirection="column" height="100%">
          <Typography color="text.tertiary" paragraph variant="caption">
            {IMAGE_DIALOG_SUBTITLES[dialogType]}
          </Typography>

          <Flex
            flexWrap="wrap"
            gap={8}
            height={isFullWidthDropCard ? 'inherit' : 'auto'}
          >
            {currentImages.map((imageData) => {
              return (
                <ImageCard
                  cardAriaLabel={DIALOG_CARD_LABELS.CURRENT}
                  description={imageData.description}
                  deleteImageFile={deleteCurrentImage}
                  imageUrl={imageData.url}
                  key={imageData.url}
                  isDescriptionVisible={isDescriptionVisible}
                  updateDescription={updateCurrentImageDescription(
                    imageData.url,
                  )}
                />
              );
            })}

            {imageFiles?.map((imageData) => {
              const { file, fileUrl } = imageData;

              return (
                <ImageUploadCard
                  ref={uploadRefCallback(fileUrl)}
                  file={file}
                  fileUrl={fileUrl}
                  deleteImageFile={deleteImageFile}
                  key={fileUrl}
                  isDescriptionVisible={isDescriptionVisible}
                  onImageUpload={onImageUpload(fileUrl)}
                  photoUploadKey={PHOTO_UPLOAD_KEYS[dialogType]}
                />
              );
            })}

            {dialogType === IMAGE_DIALOG_TYPES.PHOTOS &&
              fileValidationErrors?.map(({ errorMessage, fileName }, index) => (
                <ImageValidationErrorCard
                  errorMessage={errorMessage}
                  fileName={fileName}
                  key={`${fileName} ${index}`}
                  clearErrorMessage={clearFileValidationError(fileName)}
                />
              ))}

            {isDropAreaVisible && (
              <div css={isFullWidthDropCard && css.dropWrapper}>
                <FileDropzone
                  acceptedFileFormats={acceptedFileFormats}
                  dropFrameRef={dropFrameRef}
                  fileInputLabel={HIDDEN_INPUT_LABEL}
                  fileInputRef={fileInputRef}
                  handleDropTargetClick={getHandleDropTargetClick()}
                  handleFile={handleFile}
                  handleOnFrameDragEnter={clearDropCardErrorMessage}
                  isMultiFile={maxDropFileCount > 1}
                  resetImageFiles={resetImageFiles}
                >
                  <ImageDropCard
                    dropFrameRef={dropFrameRef}
                    handleDropTargetClick={handleDropTargetClick}
                    isFullWidthDropCard={isFullWidthDropCard}
                    errorMessage={dropCardErrorMessage}
                  />
                </FileDropzone>
              </div>
            )}
          </Flex>
        </Flex>
      </DialogContent>
      <Divider />

      <Dialog.Actions css={css.dialogActions}>
        <div>
          <Button onClick={handleCancelOnClick} color="primary" variant="text">
            Cancel
          </Button>
        </div>

        <div>
          <Button
            color="primary"
            disabled={!isAddPhotosActive}
            startIcon="camera"
            variant="text"
            onClick={handleAddPhotosOnClick}
          >
            {ADD_IMAGE_TEXT[dialogType]}
          </Button>

          <Button
            color="primary"
            disabled={isSavedButtonDisabled}
            onClick={handleSaveOnClick}
          >
            Save
          </Button>
        </div>
      </Dialog.Actions>
    </React.Fragment>
  );
};

ImageManager.propTypes = {
  closeDialog: PropTypes.func,
  dialogType: PropTypes.oneOf([
    IMAGE_DIALOG_TYPES.LOGO,
    IMAGE_DIALOG_TYPES.PHOTOS,
  ]),
  images: PropTypes.arrayOf(
    PropTypes.shape({
      description: PropTypes.string,
      clUrl: PropTypes.string,
      thumbUrl: PropTypes.string,
      url: PropTypes.string,
    }),
  ),
  updateImages: PropTypes.func,
};

export default ImageManager;
